import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";
import { businessTypeSchema } from "./clinic.ts";

// ===== Clinic update =====

const hexColor = z
	.string()
	.regex(/^#[0-9a-fA-F]{6}$/, "Hex color like #14b8a6");

export const brandingInputSchema = z
	.object({
		// R2 object key (e.g. clinics/<id>/logo.png). Server presigns to a URL
		// on read; clients never need the raw key once it's saved.
		logo_key: z.string().max(500).nullable().optional(),
		primary_color: hexColor.nullable().optional(),
		accent_color: hexColor.nullable().optional(),
		cover_image_key: z.string().max(500).nullable().optional(),
		bio_ar: z.string().max(400).nullable().optional(),
		bio_en: z.string().max(400).nullable().optional(),
	})
	.partial();
export type BrandingInput = z.infer<typeof brandingInputSchema>;

/**
 * Per-facility print settings for patient documents (lab results, and later
 * prescriptions/receipts). A single configurable layout — paper size, header
 * style, an optional footer note, and a signature/stamp line — not a template
 * designer. Stored under clinics.settings.print.
 */
export const PRINT_PAPER_SIZES = ["a4", "a5"] as const;
export const PRINT_HEADER_MODES = ["branded", "minimal"] as const;
export const printSettingsSchema = z.object({
	paper_size: z.enum(PRINT_PAPER_SIZES).optional(),
	header_mode: z.enum(PRINT_HEADER_MODES).optional(),
	footer_note: z.string().max(300).optional(),
	show_signature_line: z.boolean().optional(),
});
export type PrintSettings = z.infer<typeof printSettingsSchema>;

/** Dual-verification modes for lab values (ISO 15189 §5.7). */
export const LAB_DUAL_VERIFICATION_MODES = [
	"off",
	"critical",
	"abnormal",
] as const;
export const labDualVerificationSchema = z.enum(LAB_DUAL_VERIFICATION_MODES);
export type LabDualVerificationMode = z.infer<typeof labDualVerificationSchema>;

export const updateClinicInputSchema = z.object({
	name: bilingualStringSchema.optional(),
	business_type: businessTypeSchema.optional(),
	timezone: z.string().optional(),
	default_language: z.enum(["ar", "en"]).optional(),
	rtl_default: z.boolean().optional(),
	branding: brandingInputSchema.optional(),
	settings: z
		.object({
			default_appointment_duration_min: z
				.number()
				.int()
				.positive()
				.max(480)
				.optional(),
			default_buffer_min: z.number().int().nonnegative().max(60).optional(),
			allow_walkin: z.boolean().optional(),
			allow_online_booking: z.boolean().optional(),
			appointment_lead_time_hours: z.number().int().nonnegative().optional(),
			cancellation_window_hours: z.number().int().nonnegative().optional(),
			no_show_threshold: z.number().int().positive().optional(),
			voice_ai_enabled: z.boolean().optional(),
			whatsapp_enabled: z.boolean().optional(),
			// Lab self-service: show the patient-facing AI explanation on lab results
			// (link + printout). Default ON; an explicit false hides it everywhere.
			patient_lab_explanation: z.boolean().optional(),
			// Per-facility print layout for patient documents.
			print: printSettingsSchema.optional(),
			instagram_enabled: z.boolean().optional(),
			queue_display_enabled: z.boolean().optional(),
			public_booking_enabled: z.boolean().optional(),
			// Workflow: nightly cron marks yesterday's booked/confirmed as no_show.
			// Default ON — an explicit false opts the clinic out.
			auto_no_show: z.boolean().optional(),
			// Lab specimen lifecycle (ISO 15189 §5.4/§5.5). When ON, result entry
			// requires the order's specimen to be marked received first
			// (instrument/linked-lab sources exempt — the analyzer/lab physically
			// has the sample). Default OFF → zero behavior change.
			lab_specimen_tracking: z.boolean().optional(),
			// Dual verification (ISO 15189 §5.7). 'critical' = critical_* values
			// must be verified by a member other than the one who entered them;
			// 'abnormal' extends that to every non-normal flag. Default 'off'.
			lab_dual_verification: labDualVerificationSchema.optional(),
			// Workflow: clinics that collect the consult fee at the front desk
			// (before the doctor) get a quick "collect payment" action at check-in.
			// Default OFF.
			collect_on_checkin: z.boolean().optional(),
			// Walk-in window — when set to a future ISO timestamp, the slot
			// picker opens up [now, walk_in_open_until] for booking even if
			// the regular schedule has ended. Lets a doctor who's at the
			// clinic outside of posted hours still accept QR walk-ins.
			walk_in_open_until: z.string().datetime().nullable().optional(),
			// Public contact block shown on the /c/<slug> booking page so patients
			// can call / locate the clinic. Stored in settings (no dedicated cols).
			contact: z
				.object({
					phone: z.string().max(40),
					address: z.string().max(300),
					maps_url: z.string().max(500),
					hours_note: z.string().max(200),
				})
				.partial()
				.optional(),
		})
		.partial()
		.optional(),
});
export type UpdateClinicInput = z.infer<typeof updateClinicInputSchema>;

export const brandingLogoUploadInputSchema = z.object({
	content_type: z.enum(["image/jpeg", "image/png", "image/webp"]),
	content_length: z
		.number()
		.int()
		.positive()
		.max(2 * 1024 * 1024),
});
export type BrandingLogoUploadInput = z.infer<
	typeof brandingLogoUploadInputSchema
>;

export interface BrandingLogoUploadResponse {
	upload_url: string;
	key: string;
}

// ===== Services =====

export const clinicServiceApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: bilingualStringSchema,
	description: z
		.object({ ar: z.string().optional(), en: z.string().optional() })
		.nullable(),
	duration_minutes: z.number().int().positive(),
	buffer_minutes: z.number().int().nonnegative(),
	price: z.string(), // numeric returned as string from Postgres
	currency: z.string(),
	category: z.string().nullable(),
	color: z.string().nullable(),
	is_active: z.boolean(),
	sort_order: z.number().int(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicServiceApi = z.infer<typeof clinicServiceApiSchema>;

export const createServiceInputSchema = z.object({
	name: bilingualStringSchema,
	description: z
		.object({ ar: z.string().optional(), en: z.string().optional() })
		.optional()
		.nullable(),
	duration_minutes: z.number().int().positive().max(480).default(30),
	buffer_minutes: z.number().int().nonnegative().max(60).default(0),
	price: z.coerce.number().nonnegative().default(0),
	currency: z.string().default("IQD"),
	category: z.string().nullable().optional(),
	color: z
		.string()
		.regex(/^#[0-9a-fA-F]{6}$/, "Hex color like #14b8a6")
		.nullable()
		.optional(),
	is_active: z.boolean().default(true),
	sort_order: z.number().int().default(0),
});
export type CreateServiceInput = z.infer<typeof createServiceInputSchema>;

export const updateServiceInputSchema = createServiceInputSchema.partial();
export type UpdateServiceInput = z.infer<typeof updateServiceInputSchema>;
